import { Head, Link, router, usePage } from "@inertiajs/react";
import type {
	ColumnDef,
	OnChangeFn,
	Row,
	Table as TableTanstack,
	VisibilityState,
} from "@tanstack/react-table";
import { useLocalStorage } from "@uidotdev/usehooks";
import { format, formatDistanceToNow } from "date-fns";
import {
	Ellipsis,
	InfinityIcon,
	Info,
	LockIcon,
	PlusCircle,
	SquarePenIcon,
	Trash2Icon,
	UserRoundCogIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PaginationTable from "@/components/admin-portal/shared/data-table-pagination";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { SwitchAccountContent } from "@/components/admin-portal/switch-account-dialog";
import ToolbarTable from "@/components/admin-portal/therapist/data-table-toolbar";
import {
	ChangePasswordContent,
	DeleteTherapistAlert,
	DetailsTherapistContent,
} from "@/components/admin-portal/therapist/feature-actions";
import type { DetailsTimeline } from "@/components/admin-portal/therapist/relocation/details-address-section";
import { TherapistTagsCell } from "@/components/admin-portal/therapist/tags-column";
import { createdAtColumn } from "@/components/shared/created-at-column";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import { LoadingBasic } from "@/components/shared/loading";
import {
	ResponsiveDialog,
	type ResponsiveDialogProps,
} from "@/components/shared/responsive-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
// DISABLED - Sync from spreadsheet is no longer needed
// import {
// 	Popover,
// 	PopoverContent,
// 	PopoverTrigger,
// } from "@/components/ui/popover";
// import { Switch } from "@/components/ui/switch";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
// DISABLED - Sync from spreadsheet is no longer needed
// import { useMasterDataSync } from "@/hooks/admin-portal/use-master-data-sync";
import { getGenderIcon } from "@/hooks/use-gender";
import { useIsMobile } from "@/hooks/use-mobile";
import { getBrandBadgeVariant } from "@/lib/services";
import { getEmpStatusBadgeVariant } from "@/lib/therapists";
import { cn, generateInitials, populateQueryParams } from "@/lib/utils";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { Location } from "@/types/admin-portal/location";
import type {
	TherapistEmploymentStatus,
	TherapistEmploymentType,
	TherapistListItem,
	TherapistTableRow,
} from "@/types/admin-portal/therapist";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

export interface PageProps {
	therapists: {
		data: TherapistTableRow[];
		metadata: Metadata;
	};
	selectedTherapist: null | TherapistListItem;
	selectedTherapistAppts: {
		metadata: Metadata;
		data: { date: string; schedules: Appointment[] }[];
	} | null;
	/** optional: asked for by the details panel once it opens */
	selectedTherapistTimeline?: DetailsTimeline | null;
	filterOptions?: {
		employmentStatuses: TherapistEmploymentStatus;
		employmentTypes: TherapistEmploymentType;
		locations: Location[];
		services: { id: string; name: string; code: string }[];
	};
}
export type TableRowDataProps = Row<PageProps["therapists"]["data"][number]>;
export type TableToolbarDataProps = TableTanstack<
	PageProps["therapists"]["data"][number]
>;
export interface TherapistIndexGlobalPageProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: any;
}

export default function Index({
	therapists,
	selectedTherapist,
	selectedTherapistAppts,
	selectedTherapistTimeline,
}: PageProps) {
	const { props: globalProps, url: pageURL } =
		usePage<TherapistIndexGlobalPageProps>();
	const isMobile = useIsMobile();
	const { t } = useTranslation("translation");
	const { t: tt } = useTranslation("therapists");

	// DISABLED - Sync from spreadsheet is no longer needed
	// const onSyncComplete = useCallback(() => {
	// 	router.reload({ only: ["adminPortal", "therapists"] });
	// }, []);

	// const [useRamadanTime, setUseRamadanTime] = useState(false);
	// const [popoverOpen, setPopoverOpen] = useState(false);

	// const { isLoading, syncStatus, triggerSync, clearStatus } = useMasterDataSync(
	// 	{
	// 		syncEndpoint: `${globalProps.adminPortal.router.adminPortal.therapistManagement.index}/sync-data-master`,
	// 		statusEndpoint: `${globalProps.adminPortal.router.adminPortal.therapistManagement.index}/sync-status`,
	// 		onSyncComplete,
	// 		autoCheckOnMount: true,
	// 		syncType: "therapists_and_schedules",
	// 	},
	// );

	// * for table actions management state
	const routeTo = {
		edit: (id: number | string) => {
			const url = `${globalProps.adminPortal.router.adminPortal.therapistManagement.index}/${id}/edit`;

			router.get(url);
		},
		changePassword: (id: number | string) => {
			const url = pageURL;

			router.get(
				url,
				{ change_password: id },
				{
					only: ["adminPortal", "flash", "errors", "selectedTherapist"],
					preserveScroll: true,
					preserveState: true,
				},
			);
		},
		switchAccount: (id: number | string) => {
			router.get(
				pageURL,
				{ switch_account: id },
				{
					only: ["adminPortal", "flash", "errors", "selectedTherapist"],
					preserveScroll: true,
					preserveState: true,
				},
			);
		},
		delete: (id: number | string) => {
			const url = pageURL;

			router.get(
				url,
				{ delete: id },
				{
					only: ["adminPortal", "flash", "errors", "selectedTherapist"],
					preserveScroll: true,
					preserveState: true,
				},
			);
		},
		detail: useCallback(
			(id: string, page?: number, limit?: number) => {
				router.get(
					pageURL,
					{ details: id, appt_page: page, appt_limit: limit },
					{
						only: [
							"adminPortal",
							"flash",
							"errors",
							"selectedTherapist",
							"selectedTherapistAppts",
						],
						preserveScroll: true,
						preserveState: true,
					},
				);
			},
			[pageURL],
		),
	};
	const formDialog = useMemo<
		ResponsiveDialogProps & {
			mode: {
				isChangePasswordMode: boolean;
				isSwitchAccountMode: boolean;
				isDeleteMode: boolean;
				isDetailsMode: boolean;
			};
		}
	>(() => {
		const currentQuery = globalProps.adminPortal?.currentQuery;
		const isChangePasswordMode = !!currentQuery?.changePassword;
		const isSwitchAccountMode = !!currentQuery?.switchAccount;
		const isDeleteMode = !!currentQuery?.delete;
		const isDetailsMode = !!currentQuery?.details;
		const mode = {
			isChangePasswordMode,
			isSwitchAccountMode,
			isDeleteMode,
			isDetailsMode,
		};
		const isOpen =
			isChangePasswordMode ||
			isSwitchAccountMode ||
			isDeleteMode ||
			isDetailsMode;
		let title = "";
		let description = "";

		if (isDeleteMode) {
			title = tt("dialog.delete.title");
			description = tt("dialog.delete.description");
		}

		if (isChangePasswordMode) {
			title = tt("dialog.change_password.title");
			description = tt("dialog.change_password.description");
		}

		if (isSwitchAccountMode) {
			title = t("components.switch_account.title");
			description = t("components.switch_account.description", {
				profile: t("components.switch_account.profile.therapist"),
			});
		}

		if (isDetailsMode) {
			title = tt("dialog.details.title");
			description = tt("dialog.details.description");
		}

		return {
			title,
			description,
			isOpen,
			mode,
			onOpenChange: (value: boolean) => {
				if (!value) {
					const objQueryParams = isChangePasswordMode
						? { change_password: null }
						: isSwitchAccountMode
							? { switch_account: null }
							: isDeleteMode
								? { delete: null }
								: { details: null };
					const { fullUrl } = populateQueryParams(pageURL, objQueryParams);

					router.get(
						fullUrl,
						{},
						{
							only: ["selectedTherapist", "flash", "adminPortal", "errors"],
							preserveScroll: true,
						},
					);
				}
			},
		};
	}, [pageURL, globalProps.adminPortal?.currentQuery, t, tt]);
	// Which columns an admin hides is a per-device preference, as on the appointments page.
	// Stored as hidden ids, so a column added later starts visible.
	const [columnPreferences, saveColumnPreferences] = useLocalStorage(
		"admin-portal-therapist-columns",
		{ hiddenColumns: [] as string[] },
	);
	const columnVisibility = useMemo(
		() =>
			Object.fromEntries(
				(columnPreferences.hiddenColumns ?? []).map((id) => [id, false]),
			),
		[columnPreferences.hiddenColumns],
	);
	const onColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
		(updater) =>
			saveColumnPreferences((prev) => {
				const current = Object.fromEntries(
					(prev.hiddenColumns ?? []).map((id) => [id, false]),
				);
				const next = typeof updater === "function" ? updater(current) : updater;

				return {
					hiddenColumns: Object.keys(next).filter((id) => next[id] === false),
				};
			}),
		[saveColumnPreferences],
	);

	// the key is absent until fisiohome-backend migration 0124 adds the column
	const religionSupported = therapists.data[0]?.religion !== undefined;
	const columns = useMemo<
		ColumnDef<PageProps["therapists"]["data"][number]>[]
	>(() => {
		const items: ColumnDef<PageProps["therapists"]["data"][number]>[] = [
			{
				accessorKey: "profile",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title={tt("list.columns.profile")}
					/>
				),
				enableSorting: false,
				enableHiding: false,
				cell: ({ row }) => {
					const name = row.original.name;
					const initials = generateInitials(name);
					const registrationNumber = row.original.registrationNumber;
					const isOnline = row.original.user["isOnline?"];
					const isCurrent =
						row.original.user.email ===
						globalProps.auth.currentUser?.user.email;
					const isSuspended = row.original.user["suspended?"];

					return (
						<button
							type="button"
							title={tt("list.view_details")}
							onClick={() => routeTo.detail(row.original.id)}
							className="flex items-center w-full gap-2 text-sm text-left rounded-md cursor-pointer group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							<Avatar className="w-8 h-8 border rounded-lg bg-muted">
								<AvatarImage src="#" alt={name} />
								<AvatarFallback
									className={cn(
										"text-xs rounded-lg",
										isSuspended
											? "bg-destructive text-destructive-foreground"
											: isCurrent
												? "bg-primary text-primary-foreground"
												: isOnline
													? "bg-emerald-700 text-white"
													: "bg-muted",
									)}
								>
									{initials}
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 space-y-0.5 text-sm leading-tight text-left">
								<p className="font-bold uppercase truncate max-w-52 md:max-w-16 lg:max-w-full group-hover:underline">
									{name}
								</p>
								<p className="text-xs font-light break-all">
									{row.original.user.email}
								</p>
								<p className="text-xs font-light">#{registrationNumber}</p>
							</div>
						</button>
					);
				},
			},
		];

		if (!isMobile) {
			items.push(
				{
					accessorKey: "location",
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={tt("list.columns.location")}
						/>
					),
					enableSorting: true,
					cell: ({ row }) => {
						const activeAddress = row.original.activeAddress;
						const location = activeAddress?.location;

						return (
							<div>
								<p className="text-pretty">
									{!location
										? tt("list.not_available")
										: [location.city, location.state, location.country].join(
												", ",
											)}
								</p>
							</div>
						);
					},
				},
				{
					accessorKey: "gender",
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={tt("list.columns.gender")}
						/>
					),
					enableSorting: true,
					cell: ({ row }) => {
						const gender = row.original.gender;

						return (
							<Badge
								variant="outline"
								className="text-[10px] py-0 px-1 rounded-full"
							>
								<div className="flex items-center gap-0.5">
									{getGenderIcon(
										gender,
										"size-3.5 text-muted-foreground/75 shrink-",
									)}
									<span>{gender}</span>
								</div>
							</Badge>
						);
					},
				},
				{
					accessorKey: "type",
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={tt("list.columns.type")}
						/>
					),
					enableSorting: true,
					cell: ({ row }) => {
						const employmentType = row.original.employmentType;

						return (
							<Badge
								variant={employmentType === "KARPIS" ? "accent" : "secondary"}
								className="rounded-full text-[10px] py-0 px-1"
							>
								{employmentType}
							</Badge>
						);
					},
				},
				{
					accessorKey: "service",
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={tt("list.columns.service")}
						/>
					),
					enableSorting: true,
					cell: ({ row }) => {
						const serviceName = row.original.service.name.replaceAll("_", " ");
						const serviceCode = row.original.service.code;
						const brandBadgeVariant = getBrandBadgeVariant(serviceCode);

						return (
							<Badge
								variant="outline"
								className={cn(
									"whitespace-nowrap rounded-full text-[10px] py-0 px-1",
									brandBadgeVariant,
								)}
							>
								{serviceName}
							</Badge>
						);
					},
				},
				{
					accessorKey: "status",
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={tt("list.columns.status")}
						/>
					),
					enableSorting: true,
					cell: ({ row }) => {
						const variant = getEmpStatusBadgeVariant(
							row.original.employmentStatus,
						);

						return (
							<Badge
								className={cn("rounded-full text-[10px] py-0 px-1", variant)}
							>
								{row.original.employmentStatus}
							</Badge>
						);
					},
				},
				{
					accessorKey: "activity",
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={tt("list.columns.activity")}
						/>
					),
					enableSorting: true,
					cell: ({ row }) => {
						const isOnline = row.original.user["isOnline?"];
						const currentIP = row.original.user.currentSignInIp;
						const lastIP = row.original.user.lastSignInIp;
						const lastOnlineAt = row.original.user.lastOnlineAt;
						const isSuspended = row.original.user["suspended?"];
						const suspendedAt = row.original.user.suspendAt;
						const suspendEnd = row.original.user.suspendEnd;

						if (isSuspended) {
							return (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger>
											<DotBadgeWithLabel variant="destructive">
												<span>{tt("list.activity.suspended")}</span>
											</DotBadgeWithLabel>
										</TooltipTrigger>
										<TooltipContent side="bottom">
											<div className="flex flex-col">
												{suspendedAt && (
													<span>
														{tt("list.activity.suspend_on")}:{" "}
														<b>{format(suspendedAt, "PP")}</b>
													</span>
												)}
												<span className="flex items-center">
													{tt("list.activity.suspend_until")}:{" "}
													<b>
														{suspendEnd ? (
															format(suspendEnd, "PP")
														) : (
															<InfinityIcon className="ml-1 size-4" />
														)}
													</b>
												</span>
											</div>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							);
						}

						if (globalProps.auth.currentUser?.["isSuperAdmin?"]) {
							return (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger className="space-x-1">
											<DotBadgeWithLabel
												variant={isOnline ? "success" : "outline"}
											>
												<span>
													{isOnline
														? tt("list.activity.online")
														: tt("list.activity.offline")}
												</span>
											</DotBadgeWithLabel>
										</TooltipTrigger>
										<TooltipContent>
											{isOnline ? (
												<span>
													{tt("list.activity.current_ip")}: <b>{currentIP}</b>
												</span>
											) : (
												<div className="flex flex-col">
													<span>
														{tt("list.activity.last_ip")}: <b>{lastIP}</b>
													</span>
													{lastOnlineAt && (
														<span>
															{tt("list.activity.last_online_session")}:{" "}
															<b>
																{formatDistanceToNow(lastOnlineAt, {
																	includeSeconds: true,
																	addSuffix: true,
																})}
															</b>
														</span>
													)}
												</div>
											)}
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							);
						}

						return (
							<DotBadgeWithLabel variant={isOnline ? "success" : "outline"}>
								<span>
									{isOnline
										? tt("list.activity.online")
										: tt("list.activity.offline")}
								</span>
							</DotBadgeWithLabel>
						);
					},
				},
				...(religionSupported
					? [
							{
								accessorKey: "religion",
								header: ({ column }) => (
									<DataTableColumnHeader
										column={column}
										title={tt("list.columns.religion")}
									/>
								),
								cell: ({ row }) => {
									const religion = row.original.religion;

									return religion ? (
										<span>{tt(`enums.religion.${religion}`)}</span>
									) : (
										<span className="italic text-muted-foreground">
											{tt("enums.religion_unset")}
										</span>
									);
								},
							} satisfies ColumnDef<PageProps["therapists"]["data"][number]>,
						]
					: []),
				{
					id: "tags",
					header: tt("list.tags"),
					enableSorting: false,
					cell: ({ row }) => <TherapistTagsCell therapist={row.original} />,
				},
				{ ...createdAtColumn(tt("list.joined_at")), enableHiding: true },
			);
		}

		items.push({
			id: "actions",
			enableHiding: false,
			cell: ({ row }) => {
				// Deleting a therapist account is still super-admin only; everything else in
				// this menu is open to any admin, which is now everyone who can be here.
				const isShowDelete = !!globalProps.auth.currentUser?.["isSuperAdmin?"];

				return (
					<div className="flex items-center justify-end space-x-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="icon">
									<Ellipsis />
								</Button>
							</DropdownMenuTrigger>

							<DropdownMenuContent align="end">
								<DropdownMenuLabel>
									{tt("list.actions.label")}
								</DropdownMenuLabel>
								<DropdownMenuSeparator />

								<DropdownMenuGroup>
									<DropdownMenuItem
										onSelect={() => routeTo.detail(row.original.id)}
									>
										<Info />
										<span>{tt("list.actions.details")}</span>
									</DropdownMenuItem>

									<DropdownMenuItem
										onSelect={() => routeTo.edit(row.original.id)}
									>
										<SquarePenIcon />
										<span>{tt("list.actions.edit")}</span>
									</DropdownMenuItem>

									<DropdownMenuItem
										onSelect={() => routeTo.changePassword(row.original.id)}
									>
										<LockIcon />
										<span>{tt("list.actions.change_password")}</span>
									</DropdownMenuItem>

									<DropdownMenuItem
										onSelect={() => routeTo.switchAccount(row.original.id)}
									>
										<UserRoundCogIcon />
										<span>{t("components.switch_account.title")}</span>
									</DropdownMenuItem>
								</DropdownMenuGroup>

								{isShowDelete && (
									<>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											onSelect={() => routeTo.delete(row.original.id)}
										>
											<Trash2Icon />
											<span>{tt("list.actions.delete")}</span>
										</DropdownMenuItem>
									</>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				);
			},
		});

		return items;
	}, [
		globalProps.auth,
		isMobile,
		t,
		tt,
		routeTo.changePassword,
		routeTo.switchAccount,
		routeTo.edit,
		routeTo.delete,
		routeTo.detail,
		religionSupported,
	]);

	return (
		<>
			<Head title={tt("head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4 overflow-hidden relative">
				{/* DISABLED - Sync from spreadsheet is no longer needed
				{(globalProps?.auth?.currentUser?.["isSuperAdmin?"] ||
					globalProps?.auth?.currentUser?.["isAdminSupervisor?"]) &&
					syncStatus.message && (
						<div
							className={cn(
								"p-4 rounded-md border relative",
								syncStatus.type === "success" &&
									"bg-green-50 border-green-200 text-green-800",
								syncStatus.type === "error" &&
									"bg-red-50 border-red-200 text-red-800",
								syncStatus.type === "info" &&
									"bg-blue-50 border-blue-200 text-blue-800",
							)}
						>
							<button
								onClick={clearStatus}
								type="button"
								className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/10 transition-colors"
								aria-label="Close notification"
							>
								<X className="h-4 w-4" />
							</button>
							<div className="flex items-center gap-2 pr-8">
								{syncStatus.type === "info" && (
									<LoaderIcon className="animate-spin h-4 w-4" />
								)}
								{syncStatus.type === "success" && (
									<RefreshCcw className="h-4 w-4" />
								)}
								<span className="text-sm font-medium">
									{syncStatus.message}
								</span>
							</div>
						</div>
					)} */}
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{tt("page_title")}
						</h1>

						<p className="w-full text-sm md:w-8/12 text-pretty text-muted-foreground">
							{tt("page_description")}
						</p>
					</div>

					<div className="flex flex-col gap-2 md:flex-row">
						{/* DISABLED - Sync from spreadsheet is no longer needed
						(globalProps?.auth?.currentUser?.["isSuperAdmin?"] ||
							globalProps?.auth?.currentUser?.["isAdminSupervisor?"]) && (
							<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
								<PopoverTrigger asChild>
									<Button variant="primary-outline" disabled={isLoading}>
										{isLoading ? (
											<>
												<LoaderIcon className="animate-spin" />
												<span>{`${t("components.modal.wait")}...`}</span>
											</>
										) : (
											<>
												<RefreshCcw />
												{tt("button.sync")}
												<ChevronDown className="ml-1 h-4 w-4" />
											</>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-72" align="end">
									<div className="grid gap-4">
										<div className="space-y-1 text-sm">
											<h4 className="font-medium leading-none">Sync Options</h4>
											<p className="text-muted-foreground">
												Choose sync type and time settings
											</p>
										</div>

										<div className="space-y-3">
											<div className="flex items-center justify-between space-x-2">
												<label
													htmlFor="ramadan-time"
													className="text-sm font-medium cursor-pointer"
												>
													Use Ramadan Time
												</label>
												<Switch
													id="ramadan-time"
													checked={useRamadanTime}
													onCheckedChange={setUseRamadanTime}
												/>
											</div>
											{useRamadanTime && (
												<p className="text-xs text-muted-foreground">
													Using Ramadan schedule: 8:00 AM - 5:00 PM
												</p>
											)}
											<Separator />
											<div className="space-y-1">
												<Button
													variant="default"
													size="sm"
													className="w-full"
													disabled={isLoading}
													onClick={() => {
														triggerSync({
															employment_type_filter: "KARPIS",
															use_ramadan_time: useRamadanTime.toString(),
														});
														setPopoverOpen(false);
													}}
												>
													{isLoading ? "Syncing..." : "Sync Karyawan (KARPIS)"}
												</Button>
												<Button
													variant="outline"
													size="sm"
													className="w-full"
													disabled={isLoading}
													onClick={() => {
														triggerSync({
															employment_type_filter: "FLAT",
															use_ramadan_time: useRamadanTime.toString(),
														});
														setPopoverOpen(false);
													}}
												>
													{isLoading ? "Syncing..." : "Sync Mitra (FLAT)"}
												</Button>
											</div>
										</div>
									</div>
								</PopoverContent>
							</Popover>
						)} */}

						{(globalProps?.auth?.currentUser?.["isSuperAdmin?"] ||
							globalProps?.auth?.currentUser?.["isAdminSupervisor?"]) && (
							<Button asChild>
								<Link
									href={
										globalProps.adminPortal.router.adminPortal
											.therapistManagement.new
									}
								>
									<PlusCircle />
									{tt("button.add")}
								</Link>
							</Button>
						)}
					</div>
				</div>

				<Separator className="bg-border" />

				<DataTable
					columns={columns}
					data={therapists.data}
					columnVisibility={columnVisibility}
					onColumnVisibilityChange={onColumnVisibilityChange}
					toolbar={(table) => <ToolbarTable table={table} />}
					customPagination={(table) => (
						<PaginationTable table={table} metadata={therapists.metadata} />
					)}
				/>

				{formDialog.isOpen && (
					<>
						{selectedTherapist && formDialog.mode.isChangePasswordMode && (
							<ResponsiveDialog {...formDialog}>
								<ChangePasswordContent
									selectedTherapistAccount={selectedTherapist.user}
									forceMode={formDialog.forceMode}
									handleOpenChange={formDialog.onOpenChange}
								/>
							</ResponsiveDialog>
						)}

						{selectedTherapist && formDialog.mode.isSwitchAccountMode && (
							<ResponsiveDialog {...formDialog} dialogWidth="480px">
								<SwitchAccountContent
									profileId={selectedTherapist.id}
									currentEmail={selectedTherapist.user.email}
									checkUrl={
										globalProps.adminPortal.router.adminPortal
											.therapistManagement.checkAccount
									}
									switchUrl={
										globalProps.adminPortal.router.adminPortal
											.therapistManagement.switchAccount
									}
									onSuccess={() => {
										// one visit: drops the query (closing the dialog) and
										// refreshes the table with the new email
										const { fullUrl } = populateQueryParams(pageURL, {
											switch_account: null,
										});
										router.get(fullUrl);
									}}
								/>
							</ResponsiveDialog>
						)}

						{selectedTherapist && formDialog.mode.isDeleteMode && (
							<DeleteTherapistAlert
								{...formDialog}
								selectedTherapist={selectedTherapist}
							/>
						)}

						{formDialog.mode.isDetailsMode && (
							<ResponsiveDialog
								{...formDialog}
								dialogWidth={selectedTherapist ? "75%" : undefined}
							>
								{selectedTherapist ? (
									<DetailsTherapistContent
										therapist={selectedTherapist}
										appts={selectedTherapistAppts}
										timeline={selectedTherapistTimeline}
										onPageChange={(page, limit) =>
											routeTo.detail(selectedTherapist.id, page, limit)
										}
									/>
								) : (
									<LoadingBasic columnBased />
								)}
							</ResponsiveDialog>
						)}
					</>
				)}
			</PageContainer>
		</>
	);
}
